import type { Middleware, UnknownAction } from '@reduxjs/toolkit';
import { TagTypes } from '../../Api/Types/tagTypes';
import { bookingsApi } from '../../Features/Bookings';
import {
  bankDocumentsApi,
  type ApproveSupplierPrepaymentApContractorResultDto,
} from '../../Features/BankDocuments';
import { expenseInvoicesApi } from '../../Features/ExpenseInvoices/Service/expenseInvoicesApi';

const normalizeEntityIds = (ids: readonly number[] | undefined): number[] =>
  Array.isArray(ids)
    ? Array.from(new Set(ids.filter((id): id is number => Number.isInteger(id) && id > 0)))
    : [];

const getApprovalTargetedRefreshPayload = (
  action: UnknownAction
): ApproveSupplierPrepaymentApContractorResultDto | null => {
  if (!bankDocumentsApi.endpoints.approveSupplierPrepaymentApContractor.matchFulfilled(action)) {
    return null;
  }

  return action.payload;
};

export const approvalTargetedRefreshMiddleware: Middleware =
  (storeApi) => (next) => (action) => {
    const result = next(action);
    const payload = getApprovalTargetedRefreshPayload(action as UnknownAction);

    if (!payload) {
      return result;
    }

    const promotedExpenseInvoiceIds = normalizeEntityIds(payload.promotedExpenseInvoiceIds);
    const affectedBookingIds = normalizeEntityIds(payload.affectedBookingIds);

    if (promotedExpenseInvoiceIds.length > 0) {
      storeApi.dispatch(
        expenseInvoicesApi.util.invalidateTags(
          promotedExpenseInvoiceIds.map((id) => ({
            type: TagTypes.ExpenseInvoice as const,
            id,
          }))
        )
      );
    }

    if (affectedBookingIds.length > 0) {
      storeApi.dispatch(
        bookingsApi.util.invalidateTags(
          [
            {
              type: TagTypes.Booking as const,
              id: TagTypes.List,
            },
            ...affectedBookingIds.map((id) => ({
              type: TagTypes.Booking as const,
              id,
            })),
          ]
        )
      );
    }

    return result;
  };
